'use client';

import { useEffect, useState } from 'react';

// How long the "[Symbol] background chosen" confirmation stays up after a
// purposeful background commit.
const SNACKBAR_MS = 3500;

function titleCase(s) {
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : s;
}

/**
 * Transient confirmation that a curated background was chosen on the strength of
 * its primary symbol (selectLayout stage 1) — the announced symbol IS the
 * background's primary. Non-primary (symbols-list) matches and the no-match
 * default are logged by the host instead, not surfaced here. Driven by the
 * committed layout: when `backgroundId` changes and carries a `symbolName`
 * (the matched symbol's, from the catalog), the toast shows for
 * SNACKBAR_MS; otherwise it shows nothing.
 *
 * Inline styles only, so it renders in hosts without Tailwind (tapp).
 */
export default function LayoutSnackbar({ backgroundId, symbolName }) {
  const [message, setMessage] = useState(null);
  // Re-arm only when the committed background actually changes, so unrelated
  // playhead re-renders don't reset the dismiss timer.
  const [shownFor, setShownFor] = useState(null);
  if (backgroundId && symbolName && shownFor !== backgroundId) {
    setShownFor(backgroundId);
    setMessage(`${titleCase(symbolName)} background chosen`);
  }
  useEffect(() => {
    if (!message) return undefined;
    const t = setTimeout(() => setMessage(null), SNACKBAR_MS);
    return () => clearTimeout(t);
  }, [message, shownFor]);

  if (!message) return null;

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '5%',
        left: '50%',
        transform: 'translateX(-50%)',
        maxWidth: '80%',
        padding: '0.5rem 1.1rem',
        borderRadius: '9999px',
        background: 'rgba(15, 23, 42, 0.85)',
        color: '#f8fafc',
        fontSize: '0.875rem',
        fontWeight: 600,
        letterSpacing: '0.01em',
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.35)',
        backdropFilter: 'blur(4px)',
        WebkitBackdropFilter: 'blur(4px)',
        whiteSpace: 'nowrap',
        pointerEvents: 'none',
        zIndex: 50,
        animation: 'tapestry-snackbar-in 240ms ease-out',
      }}
    >
      {message}
    </div>
  );
}
